import { API_URL, REQUEST_TIMEOUT_MS, HEALTHCHECK_PATH } from "../config";
import { TokenService } from "./tokenService";
import { refreshToken } from "./auth";
import { debugLog, logger, sendToTerminal } from "./terminalLogger";
import { getShortSsoToken, fetchWithSsoRetry, getBaseBearer } from "./toolsSso";
import { LogCategory, getLogLevel, LogLevel } from "../config/logging";
import { notify } from "./globalNotify";
import { getTenantId } from "../utils/getTenantId";
import { isMissingTokenResponse } from "./apiErrorClassifier";
import { readEffectiveAccessToken } from "./auth/waitForAuthReady";
import { QuotationOperationContextStore } from "../components/Tools/QuotationCalculator/services/quotationOperationContext";

/**
 * Erro padronizado para chamadas à API HTTP.
 * Encapsula o status HTTP, a classificação (offline/cors/timeout/http/unknown) e dados opcionais retornados pelo backend.
 */
export class APIError extends Error {
	constructor(
		public status: number,
		message: string,
		public kind:
			| "offline"
			| "cors"
			| "timeout"
			| "http"
			| "unknown" = "unknown",
		public data?: any,
	) {
		super(message);
		this.name = "APIError";
	}
}

function logRequest(method: string, endpoint: string, body?: any) {
	const level = getLogLevel();
	const payloadSize = body
		? typeof body === "string"
			? body.length
			: JSON.stringify(body).length
		: 0;

	// URL completa para logs
	const fullUrl = `${API_URL}${endpoint}`;

	let logData: any;

	// Nível 1: apenas resumo
	if (level === LogLevel.MINIMAL) {
		logData = { method, endpoint, url: fullUrl };
	}
	// Nível 2: resumo + payload organizado
	else if (level === LogLevel.OPERATIONAL) {
		logData = {
			method,
			endpoint,
			url: fullUrl,
			payloadSize,
			...(body && typeof body === "object"
				? {
					payload: {
						// Apenas campos principais organizados
						...(body.pedidoId ? { pedidoId: body.pedidoId } : {}),
						...(body.itemId ? { itemId: body.itemId } : {}),
						...(body.action ? { action: body.action } : {}),
					},
				}
				: {}),
		};
	}
	// Nível 3: completo
	else {
		logData = {
			method,
			endpoint,
			url: fullUrl,
			payloadSize,
			maskedHeaders: true,
			body: body
				? typeof body === "string"
					? body.substring(0, 200)
					: body
				: undefined,
		};
	}

	sendToTerminal("📤 [API] REQUEST", logData, LogCategory.API);
	debugLog("API_REQUEST", logData);
}

function logResponse(
	method: string,
	endpoint: string,
	statusOrError: number | string,
	timeMs: number,
) {
	const level = getLogLevel();

	// URL completa para logs
	const fullUrl = `${API_URL}${endpoint}`;

	let logData: any;

	// Nível 1: apenas resumo
	if (level === LogLevel.MINIMAL) {
		logData = {
			method,
			endpoint,
			url: fullUrl,
			status: typeof statusOrError === "number" ? statusOrError : undefined,
		};
	}
	// Nível 2: resumo + dados organizados
	else if (level === LogLevel.OPERATIONAL) {
		logData = {
			method,
			endpoint,
			url: fullUrl,
			status: typeof statusOrError === "number" ? statusOrError : undefined,
			error: typeof statusOrError === "string" ? statusOrError : undefined,
			durationMs: timeMs,
		};
	}
	// Nível 3: completo
	else {
		logData = {
			method,
			endpoint,
			url: fullUrl,
			status: typeof statusOrError === "number" ? statusOrError : undefined,
			error: typeof statusOrError === "string" ? statusOrError : undefined,
			durationMs: timeMs,
		};
	}

	sendToTerminal("📥 [API] RESPONSE", logData, LogCategory.API);
	debugLog("API_RESPONSE", logData);
}

function logError(
	method: string,
	endpoint: string,
	error: any,
	startedAt?: number,
) {
	const durationMs = startedAt
		? Math.round(performance.now() - startedAt)
		: undefined;
	const errorMessage = error instanceof Error ? error.message : String(error);

	// URL completa para logs
	const fullUrl = `${API_URL}${endpoint}`;

	// Erros sempre são logados (categoria ERROR)
	const logData = {
		method,
		endpoint,
		url: fullUrl,
		error: errorMessage,
		durationMs,
	};

	sendToTerminal("❌ [API] ERROR", logData, LogCategory.ERROR);
	debugLog("API_RESPONSE", logData);
}

async function handleResponse(response: Response) {
	// Log detalhado ANTES de processar resposta
	console.log("📋 [API] handleResponse chamado - CONSOLE", {
		url: response.url,
		status: response.status,
		statusText: response.statusText,
		ok: response.ok,
		type: response.type,
		redirected: response.redirected,
	});

	let data: any;
	let rawText: string = "";

	try {
		// Ler como texto primeiro para poder logar
		rawText = await response.text();
		console.log(
			"📄 [API] Resposta raw (primeiros 500 chars) - CONSOLE",
			rawText.substring(0, 500),
		);

		// Tentar parsear JSON
		try {
			data = JSON.parse(rawText);
			console.log("✅ [API] JSON parseado com sucesso - CONSOLE", {
				hasSuccess: "success" in data,
				success: data.success,
				hasData: "data" in data,
				hasError: "error" in data,
				hasMessage: "message" in data,
				dataKeys: Object.keys(data),
			});
		} catch (parseError) {
			console.error("❌ [API] Erro ao parsear JSON - CONSOLE", {
				error: parseError,
				rawTextPreview: rawText.substring(0, 200),
			});
			data = {
				_rawText: rawText.substring(0, 200),
				_parseError:
					parseError instanceof Error ? parseError.message : String(parseError),
			};
		}
	} catch (textError) {
		console.error(
			"❌ [API] Erro ao ler resposta como texto - CONSOLE",
			textError,
		);
		data = {
			_readError:
				textError instanceof Error ? textError.message : String(textError),
		};
	}

	if (!response.ok) {
		if (response.status === 401) {
			logger.warn("[API] Token expirado ou inválido - Tentando renovar");
			throw new APIError(401, data.message || "Token expirado", "http", data);
		}
		// Backends antigos respondem 403 para token AUSENTE (semanticamente 401).
		// Normaliza para 401 para acionar o refresh + retry existente — sem isso o
		// cold-start do embedded fica irrecuperável (menu sem permissões).
		if (isMissingTokenResponse(response.status, data)) {
			logger.warn(
				"[API] 403 de token ausente — normalizado para 401 (refresh/retry)",
			);
			throw new APIError(
				401,
				data.message || data.msg || "Token não fornecido",
				"http",
				data,
			);
		}
		// Log detalhado para erros 500 para ajudar no debug
		if (response.status === 500) {
			console.error("[API] Erro 500 do servidor - CONSOLE:", {
				url: response.url,
				status: response.status,
				statusText: response.statusText,
				data: data,
				dataKeys: Object.keys(data),
				error: data.error,
				message: data.message,
				rawTextPreview: rawText.substring(0, 300),
			});
			logger.error("[API] Erro interno do servidor (500)", {
				endpoint: response.url,
				message: data.message || data.error || "Erro interno do servidor",
				data: data,
			});
		}

		// ✅ DEBUG: Log para outros erros HTTP (4xx, etc)
		if (
			response.status >= 400 &&
			response.status !== 401 &&
			response.status !== 500
		) {
			logger.warn(`[API] Erro HTTP ${response.status}`, {
				url: response.url,
				status: response.status,
				statusText: response.statusText,
				message: data.message || data.error,
				dataKeys: Object.keys(data),
			});
			sendToTerminal(
				`[API] Erro HTTP ${response.status}`,
				{
					url: response.url,
					status: response.status,
					message: data.message || data.error,
					data: data,
				},
				LogCategory.ERROR,
			);
		}
		throw new APIError(
			response.status,
			data.message || data.error || "Erro na requisição",
			"http",
			data,
		);
	}
	return data;
}

function withTimeout<T>(
	promise: Promise<T>,
	ms = REQUEST_TIMEOUT_MS,
): Promise<T> {
	const controller = new AbortController();
	const id = setTimeout(() => controller.abort(), ms);
	// Tipagem específica para fetch abaixo
	return new Promise<T>((resolve, reject) => {
		promise
			.then((v) => {
				clearTimeout(id);
				resolve(v);
			})
			.catch((e) => {
				clearTimeout(id);
				if (e && e.name === "AbortError") {
					reject(new APIError(0, "Tempo limite excedido", "timeout"));
				} else {
					reject(e);
				}
			});
	});
}

// Variável para controlar se já está tentando renovar o token
let isRefreshing = false;
// Fila de requisições pendentes aguardando renovação do token
let refreshQueue: Array<() => void> = [];

// Função para processar a fila de requisições pendentes
function processQueue(success: boolean) {
	refreshQueue.forEach((callback) => callback());
	refreshQueue = [];
}

/**
 * Executa uma chamada HTTP para a API, anexando automaticamente o token JWT quando disponível
 * e realizando a renovação do token (refresh) em caso de 401, com reexecução idempotente da requisição.
 * @param endpoint Caminho relativo (ex.: "/auth/me") que será concatenado a API_URL
 * @param options Opções do fetch RequestInit
 * @returns Corpo JSON já convertido quando a resposta for ok
 * @throws {APIError} Em erros HTTP, timeout, CORS/offline ou quando a renovação do token falhar
 */
/**
 * Cliente HTTP principal da API.
 * Anexa token JWT automaticamente e renova em caso de 401.
 * Para rotas /tools/*, usa automaticamente o token SSO.
 * @param endpoint Caminho relativo (ex.: "/auth/me")
 * @param options Opções do fetch
 */
export interface ApiOverrideOptions {
	/** Sobrescreve o token JWT (contexto de operação cross-tenant). */
	accessTokenOverride?: string;
	/** Sobrescreve o tenant (X-Tenant-Id) para esta requisição. */
	tenantIdOverride?: string;
}

export async function api(
	endpoint: string,
	options: RequestInit = {},
	override: ApiOverrideOptions = {},
) {
	// Detectar se é uma rota de tools que requer token SSO
	const isToolsRoute = endpoint.startsWith("/tools/");

	// Se for rota de tools, delegar para fetchWithSsoRetry
	if (isToolsRoute) {
		try {
			debugLog("API_TOOLS_ROUTE_DETECTED", { endpoint, usesSsoToken: true });
			const url = `${API_URL}${endpoint}`;
			const response = await fetchWithSsoRetry(url, options, getBaseBearer());

			// Verificar se a resposta é ok
			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new APIError(
					response.status,
					data.message || "Erro na requisição",
					"http",
					data,
				);
			}

			return await response.json();
		} catch (error) {
			// Se for erro de fetch, converter para APIError
			if (error instanceof APIError) {
				throw error;
			}
			throw new APIError(0, String(error), "unknown");
		}
	}

	// Se a requisição é para renovar o token, não tentamos renovar novamente
	const isRefreshRequest =
		endpoint === "/auth/refresh" || endpoint === "/auth/cdr/refresh";
	const method = options.method || "GET";
	const startTime = performance.now();

	// Contexto de operação roteado (cliente cross-tenant): quando ativo, as chamadas
	// de DADOS herdam token/tenant do destino automaticamente — assim o subsistema de
	// salvamento (que usa api() genérico) roda no tenant do pedido, sem threading manual.
	// Denylist: /auth e /launcher-session SEMPRE usam a sessão global (nunca herdam).
	const GLOBAL_ONLY_PREFIXES = ["/auth", "/launcher-session"];
	const isGlobalOnlyRoute = GLOBAL_ONLY_PREFIXES.some((p) =>
		endpoint.startsWith(p),
	);
	const hasExplicitOverride =
		override.accessTokenOverride !== undefined ||
		override.tenantIdOverride !== undefined;
	const operationOverride =
		!hasExplicitOverride && !isGlobalOnlyRoute
			? QuotationOperationContextStore.getOverride()
			: null;
	const effectiveAccessTokenOverride =
		override.accessTokenOverride ?? operationOverride?.accessToken;
	const effectiveTenantIdOverride =
		override.tenantIdOverride ?? operationOverride?.tenantId;

	// Headers padrão com suporte a multi-tenant.
	// Override de tenant (cliente roteado) tem precedência sobre o global.
	const tenantId = effectiveTenantIdOverride ?? getTenantId();
	const defaultHeaders: Record<string, string> = {
		"Content-Type": "application/json",
		Accept: "application/json",
		Origin: window.location.origin,
		...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
	};

	// Tenta obter o token de acesso
	let token = TokenService.getAccessToken();

	// Configura a requisição
	const getRequestConfig = () => {
		// Override de token (contexto de operação) tem precedência. Caso contrário
		// usa o token efetivo: canônica cdr:auth:accessToken → legada → mirror.
		token = effectiveAccessTokenOverride ?? readEffectiveAccessToken();

		return {
			...options,
			mode: "cors",
			credentials: "include",
			headers: {
				...defaultHeaders,
				...(token ? { Authorization: `Bearer ${token}` } : {}),
				...options.headers,
			},
		} as RequestInit;
	};

	// Cria a configuração inicial
	let config = getRequestConfig();
	const performFetch = async (requestConfig: RequestInit) => {
		const timeoutController = new AbortController();
		const externalSignal = requestConfig.signal;
		const onExternalAbort = () => timeoutController.abort();
		if (externalSignal) {
			if (externalSignal.aborted) {
				timeoutController.abort();
			} else {
				externalSignal.addEventListener("abort", onExternalAbort, {
					once: true,
				});
			}
		}

		const timer = setTimeout(
			() => timeoutController.abort(),
			REQUEST_TIMEOUT_MS,
		);

		try {
			return await fetch(`${API_URL}${endpoint}`, {
				...requestConfig,
				signal: timeoutController.signal,
			});
		} finally {
			clearTimeout(timer);
			externalSignal?.removeEventListener("abort", onExternalAbort);
		}
	};

	try {
		logRequest(method, endpoint, options.body);

		// Primeiro, tenta fazer a requisição normal
		try {
			const response = await performFetch(config);
			const data = await handleResponse(response);
			const timeMs = Math.round(performance.now() - startTime);
			logResponse(method, endpoint, response.status, timeMs);
			return data;
		} catch (error) {

			// Com token de operação (cliente roteado) NÃO renovamos o token global:
			// o override é uma sessão separada e curta. Propaga o 401 direto.
			if (
				error instanceof APIError &&
				error.status === 401 &&
				effectiveAccessTokenOverride
			) {
				throw error;
			}

			// Se for erro 401 (não autorizado) e não estamos na rota de refresh, tenta renovar o token
			if (
				error instanceof APIError &&
				error.status === 401 &&
				!isRefreshRequest
			) {
				logger.info("[API] Token expirado, tentando renovar...");

				// Se já está renovando, adiciona esta requisição à fila
				if (isRefreshing) {
					logger.debug(
						"[API] Outra renovação em andamento, adicionando à fila",
					);
					return new Promise((resolve, reject) => {
						refreshQueue.push(() => {
							// Refaz a requisição com o novo token
							api(endpoint, options, override).then(resolve).catch(reject);
						});
					});
				}

				isRefreshing = true;

				try {
					// Tenta renovar o token
					const refreshed = await refreshToken();

					// Processa a fila de requisições pendentes
					processQueue(refreshed);

					if (refreshed) {
						logger.info("[API] Token renovado, reexecutando requisição");
						// Atualiza a configuração com o novo token
						config = getRequestConfig();
						// Refaz a requisição original com o novo token
						const retryResponse = await performFetch(config);
						const data = await handleResponse(retryResponse);
						const timeMs = Math.round(performance.now() - startTime);
						logResponse(method, endpoint, retryResponse.status, timeMs);
						return data;
					} else {
						logger.error(
							"[API] Falha ao renovar token, redirecionando para login",
						);

						// Notificar usuário antes do redirect
						notify({
							type: "error",
							title: "Sessão Expirada",
							message:
								"Sua sessão expirou. Você será redirecionado para o login.",
							sticky: true,
						});

						// Pequeno delay para usuário ver a notificação
						setTimeout(() => {
							try {
								// Salvar rota pretendida para retorno pós-login
								sessionStorage.setItem(
									"intendedRoute",
									window.location.pathname + window.location.search,
								);
							} catch { }
							try {
								// Limpar tokens/estado de sessão antes do redirect
								TokenService.clearTokens();
							} catch { }
							window.location.href = "/";
						}, 500);

						throw new APIError(401, "Sessão expirada, faça login novamente");
					}
				} finally {
					isRefreshing = false;
				}
			}
			// Classificação: offline/CORS/timeout
			if (error && (error as any).name === "AbortError") {
				throw new APIError(0, "Tempo limite excedido", "timeout");
			}
			if (error instanceof TypeError && error.message === "Failed to fetch") {
				throw new APIError(0, "Falha de rede (offline/CORS)", "offline");
			}
			if (error instanceof APIError && [404, 400].includes(error.status)) {
				throw error
			}
			throw error;
		}
	} catch (error) {
		logError(method, endpoint, error, startTime);
		throw error;
	}
}

export interface APIWithETagResult<T = any> {
	data: T | null;
	status: number;
	etag?: string | null;
	notModified: boolean;
}

/**
 * Variante do cliente HTTP que expõe ETag e suporta If-None-Match por URL.
 * Não quebra api(); usa mesma lógica de token/refresh, mas retorna cabeçalhos e 304.
 */
export async function apiWithETag(
	endpoint: string,
	options: RequestInit = {},
	opts?: { ifNoneMatch?: string; authTokenOverride?: string },
): Promise<APIWithETagResult> {
	const isRefreshRequest =
		endpoint === "/auth/refresh" || endpoint === "/auth/cdr/refresh";
	const method = options.method || "GET";
	const startTime = performance.now();

	// Headers padrão com suporte a multi-tenant
	const tenantId = getTenantId();
	const defaultHeaders: Record<string, string> = {
		"Content-Type": "application/json",
		Accept: "application/json",
		Origin: window.location.origin,
		...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
	};

	let token = opts?.authTokenOverride || TokenService.getAccessToken();

	const getRequestConfig = () => {
		token = opts?.authTokenOverride || TokenService.getAccessToken();
		const headers: Record<string, string> = {
			...defaultHeaders,
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...(options.headers as any),
		};
		if (opts?.ifNoneMatch) headers["If-None-Match"] = opts.ifNoneMatch;
		return {
			...options,
			mode: "cors",
			credentials: "include",
			headers,
		} as RequestInit;
	};

	let config = getRequestConfig();

	try {
		logRequest(method, endpoint, options.body);

		const execFetch = async (): Promise<APIWithETagResult> => {
			const fullUrl = `${API_URL}${endpoint}`;

			// Log detalhado antes de fazer o fetch
			console.log("🌐 [API] Executando fetch - CONSOLE", {
				method,
				endpoint,
				fullUrl,
				hasToken: !!token,
				tokenLength: token?.length || 0,
				headers: Object.keys(config.headers || {}),
				ifNoneMatch: opts?.ifNoneMatch || "não enviado",
			});

			const controller = new AbortController();
			const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

			try {
				console.log("📡 [API] Chamando fetch() agora - CONSOLE", fullUrl);
				const response = await fetch(fullUrl, {
					...config,
					signal: controller.signal,
				});
				clearTimeout(timer);

				console.log("📥 [API] Resposta recebida - CONSOLE", {
					status: response.status,
					statusText: response.statusText,
					ok: response.ok,
					url: response.url,
					type: response.type,
					redirected: response.redirected,
				});

				const etag = response.headers.get("ETag");
				const timeMs = Math.round(performance.now() - startTime);

				if (response.status === 304) {
					logResponse(method, endpoint, response.status, timeMs);
					return { data: null, status: 304, etag, notModified: true };
				}

				// Para outras respostas, usa handleResponse para validar .ok e extrair JSON
				const data = await handleResponse(response);
				logResponse(method, endpoint, response.status, timeMs);
				return { data, status: response.status, etag, notModified: false };
			} catch (fetchError) {
				clearTimeout(timer);
				console.error("❌ [API] Erro no fetch - CONSOLE", {
					error: fetchError,
					endpoint,
					fullUrl,
					errorType: fetchError?.constructor?.name,
					errorMessage:
						fetchError instanceof Error
							? fetchError.message
							: String(fetchError),
				});
				throw fetchError;
			}
		};

		try {
			return await execFetch();
		} catch (error) {
			if (
				error instanceof APIError &&
				error.status === 401 &&
				!isRefreshRequest
			) {
				if (isRefreshing) {
					return new Promise((resolve, reject) => {
						refreshQueue.push(() => {
							apiWithETag(endpoint, options, opts).then(resolve).catch(reject);
						});
					});
				}
				isRefreshing = true;
				try {
					const refreshed = await refreshToken();
					processQueue(refreshed);
					if (refreshed) {
						config = getRequestConfig();
						return await execFetch();
					} else {
						// Notificar usuário antes do redirect
						notify({
							type: "error",
							title: "Sessão Expirada",
							message:
								"Sua sessão expirou. Você será redirecionado para o login.",
							sticky: true,
						});

						// Pequeno delay para usuário ver a notificação
						setTimeout(() => {
							try {
								sessionStorage.setItem(
									"intendedRoute",
									window.location.pathname + window.location.search,
								);
							} catch { }
							try {
								TokenService.clearTokens();
							} catch { }
							window.location.href = "/";
						}, 500);

						throw new APIError(401, "Sessão expirada, faça login novamente");
					}
				} finally {
					isRefreshing = false;
				}
			}
			if (error && (error as any).name === "AbortError") {
				throw new APIError(0, "Tempo limite excedido", "timeout");
			}
			if (error instanceof TypeError && error.message === "Failed to fetch") {
				throw new APIError(0, "Falha de rede (offline/CORS)", "offline");
			}
			throw error;
		}
	} catch (error) {
		logError(method, endpoint, error, startTime);
		throw error;
	}
}

/**
 * Testa a conexão básica com a API (health root).
 * @returns Objeto de sucesso/erro e versão
 */
export async function testConnection(): Promise<{
	success: boolean;
	version?: string;
	message?: string;
	error?: string;
}> {
	// Healthcheck tolerante: considera sucesso quando a resposta HTTP é ok, mesmo sem JSON
	try {
		const path = HEALTHCHECK_PATH || "/";
		const tenantId = getTenantId();
		const response = await fetch(`${API_URL}${path}`, {
			method: "GET",
			headers: {
				Accept: "application/json",
				Origin: window.location.origin,
				...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
			},
			mode: "cors",
		});

		const contentType = response.headers.get("content-type") || "";
		let version: string | undefined = undefined;
		let message: string | undefined = undefined;

		if (contentType.includes("application/json")) {
			const data = await response.json().catch(() => ({}));
			version = data.version;
			message = data.message || "ok";
		} else {
			// Conteúdo não-JSON (ex.: HTML). Tratar como sucesso se HTTP ok
			message = response.ok ? "ok" : "erro";
		}

		if (response.ok) {
			return { success: true, version, message };
		}

		return { success: false, error: `HTTP ${response.status}` };
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : "Erro ao testar conexão",
		};
	}
}

// ========= NOVOS: Health e endpoint público de teste =========
export async function health(): Promise<{ message: string; version?: string }> {
	const path = HEALTHCHECK_PATH || "/";
	const tenantId = getTenantId();
	try {
		const res = await fetch(`${API_URL}${path}`, {
			method: "GET",
			headers: {
				Accept: "application/json",
				...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
			},
			mode: "cors",
		});
		if (!res.ok) {
			// Tratar como indisponível sem lançar erro e sem logar no console
			return { message: "unavailable" };
		}
		const data = await res.json().catch(() => ({ message: "ok" }));
		return data;
	} catch {
		// Silencioso: retornar indisponível
		return { message: "unavailable" };
	}
}

export async function publicTest(): Promise<{
	success: boolean;
	message: string;
	origin?: string;
	time?: string;
}> {
	const tenantId = getTenantId();
	const res = await fetch(`${API_URL}/test`, {
		method: "GET",
		headers: {
			Accept: "application/json",
			...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
		},
		mode: "cors",
	});
	const data = await res.json().catch(() => ({}));
	return data;
}

export async function testApiEndpoints() {
	// Removidos logs verborrágicos; função mantida para uso manual
	const tenantId = getTenantId();

	const endpoints = [
		{ path: "/", method: "GET", description: "Health Check" },
		{
			path: "/auth/cdr",
			method: "POST",
			description: "Autenticação (nova)",
		},
		{ path: "/orcamentos", method: "GET", description: "Lista de Orçamentos" },
		{
			path: "/regras/salvamento/test",
			method: "GET",
			description: "Regras de Salvamento",
		},
	];

	for (const endpoint of endpoints) {
		try {
			const startTime = performance.now();

			// Primeira tentativa com CORS
			try {
				const response = await fetch(`${API_URL}${endpoint.path}`, {
					method: endpoint.method,
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Origin: window.location.origin,
						...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
					},
					mode: "cors",
					credentials: "include",
				});

				// Sem logs
			} catch (error) {
				// Se falhar com CORS, tenta sem CORS
				const noCorsTry = await fetch(`${API_URL}${endpoint.path}`, {
					method: endpoint.method,
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
					},
					mode: "no-cors",
				});

				// Sem logs
			}
		} catch (error) {
			// Silencioso
		}
	}
}

// ===== Notificações WebView → API =====
export async function notifyArquivoAberto(
	orcId: number | string,
	oitId: number | string,
	idempotencyKey?: string,
): Promise<{ success: boolean; data?: any; message?: string }> {
	const body = { orc_id: Number(orcId) || 0, oit_id: Number(oitId) || 0 };
	const headers = {
		...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
	} as Record<string, string>;
	try {
		const data: any = await api("/arquivos/webview/arquivo-aberto", {
			method: "PATCH",
			headers,
			body: JSON.stringify(body),
		});
		return { success: data?.success ?? true, data };
	} catch (error: any) {
		return {
			success: false,
			message: error?.message || "Falha ao notificar arquivo aberto",
		};
	}
}

// ===== Produção: atualizar estágio de produção =====
export async function updateProductionStage(
	oitId: number | string,
	body: {
		orc_id: number;
		ost_id: number;
		oit_id: number;
		user_usucadastro: number;
		sector: number;
		nextSectorProduction: { sectorProduction: number; sectorOrcamento: number };
		id_printer_log?: number;
		impr_status?: string;
		action_status?: number;
		stop_orc?: boolean;
		source?: "website" | "CorelDraw";
	},
): Promise<{ success: boolean; data?: any; message?: string }> {
	try {
		const data: any = await api(`/update-production-stage/${oitId}`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		const msg = (data?.msg || data?.message || "").toString();
		return { success: true, data, message: msg };
	} catch (error: any) {
		return {
			success: false,
			message: error?.message || "Falha ao atualizar estágio de produção",
		};
	}
}
